import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { navLinks } from '../data/megaMenuData';
import DesktopMegaMenu from './navbar/DesktopMegaMenu';
import MobileMenu from './navbar/MobileMenu';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const [expandedSubcategories, setExpandedSubcategories] = useState({});
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Track scroll; auto-close dropdown on scroll when it's open.
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      setIsProductsDropdownOpen((open) => {
        if (!open) return open; // early-exit: no state change when already closed
        setExpandedSubcategories({});
        return false;
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!isProductsDropdownOpen) return undefined;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProductsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProductsDropdownOpen]);

  const toggleProductsDropdown = (e) => {
    e.preventDefault();
    setIsProductsDropdownOpen((open) => !open);
  };

  const closeDropdown = () => setIsProductsDropdownOpen(false);

  const toggleSubcategory = (name) =>
    setExpandedSubcategories((prev) => ({ ...prev, [name]: !prev[name] }));

  return (
    <nav
      className={`fixed w-full top-0 z-50 transition-all duration-500 ${
        isScrolled || isMobileMenuOpen
          ? 'bg-white/95 backdrop-blur-xl shadow-lg shadow-cyan-500/5 border-b border-cyan-500/10'
          : 'bg-white/70 backdrop-blur-md md:bg-transparent md:backdrop-blur-none'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="https://customer-assets.emergentagent.com/job_add4c97a-cab7-4cb4-a1cf-0b5112bb0652/artifacts/zur5e9w0_Stallion%20Bulktech.png"
              alt="Stallion Bulktech"
              className="h-11 md:h-12 w-auto object-contain transition-all duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <div
                key={link.path}
                className="relative"
                ref={link.hasDropdown ? dropdownRef : null}
              >
                {link.hasDropdown ? (
                  <button
                    onClick={toggleProductsDropdown}
                    className={`relative px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 flex items-center gap-1 ${
                      location.pathname === link.path ? 'text-cyan-600' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {location.pathname === link.path && (
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 backdrop-blur-sm border border-cyan-500/20 rounded-xl" />
                    )}
                    <span className="relative z-10">{link.name}</span>
                    <ChevronDown className={`h-4 w-4 relative z-10 transition-transform duration-300 ${isProductsDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                ) : (
                  <Link
                    to={link.path}
                    className={`relative px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 flex items-center gap-1 ${
                      location.pathname === link.path ? 'text-cyan-600' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {location.pathname === link.path && (
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 backdrop-blur-sm border border-cyan-500/20 rounded-xl" />
                    )}
                    <span className="relative z-10">{link.name}</span>
                  </Link>
                )}

                {link.hasDropdown && isProductsDropdownOpen && (
                  <DesktopMegaMenu
                    onClose={closeDropdown}
                    onMouseLeave={closeDropdown}
                    expandedSubcategories={expandedSubcategories}
                    onToggleSubcategory={toggleSubcategory}
                  />
                )}
              </div>
            ))}
            <Link to="/contact" className="ml-4 relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl blur opacity-40 group-hover:opacity-60 transition-opacity" />
              <div className="relative px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-amber-500/30 transition-all duration-300 transform group-hover:scale-105">
                Get Quote
              </div>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden relative group p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Toggle menu"
            data-testid="mobile-menu-button"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-lg blur group-hover:blur-md transition-all" />
            <div className="relative text-gray-700 p-1 rounded-lg hover:bg-cyan-50 transition-colors">
              {isMobileMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
            </div>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && <MobileMenu onClose={() => setIsMobileMenuOpen(false)} />}
    </nav>
  );
};

export default Navbar;
